/**
 * CR-8681 (E2E TC-275): who is offered Delete on a document.
 *
 * The server decides (`DocumentViewSet.perform_destroy`): the uploader, a
 * Helix user, or a customer admin for documents on their portal. These helpers
 * only choose which rows show the action, so nobody is offered a Delete the
 * server will refuse, and a supplier gets it on its own uploads.
 */
import type { Document } from '../../lib/api';

/** Per-document check the document views apply before showing Delete. */
export type CanDeleteDocument = (doc: Document) => boolean;

/**
 * The id of the user who uploaded a document.
 *
 * The list API sends `uploaded_by` as a user id; the detail API nests the
 * user. Both are read so the check holds wherever the document came from.
 *
 * @param doc - the document
 * @returns the uploader's user id, or null when unknown
 */
export function uploaderId(doc: Document): number | null {
  const raw: unknown = doc.uploaded_by;
  if (typeof raw === 'number') return raw;
  if (raw && typeof raw === 'object' && typeof (raw as { id?: unknown }).id === 'number') {
    return (raw as { id: number }).id;
  }
  return null;
}

/**
 * Build the Delete check for the Documents page.
 *
 * The customer side keeps the Delete it has always had (a customer admin may
 * curate every document on their portal; the server refuses the rest). Anyone
 * else — a supplier — is offered it on the documents they uploaded only.
 *
 * @param isCustomer - the user is on the customer side of the portal
 * @param userId - the signed-in user's id
 * @returns the per-document check
 */
export function documentDeleteRule(isCustomer: boolean, userId: number | undefined): CanDeleteDocument {
  if (isCustomer) return () => true;
  return (doc) => userId !== undefined && uploaderId(doc) === userId;
}

/**
 * Whether a view shows Delete for one document.
 *
 * @param onDelete - the view's delete handler; absent means no Delete at all
 * @param canDelete - the per-document check; absent means every document
 * @param doc - the document
 * @returns true when Delete is shown
 */
export function showsDelete(
  onDelete: ((doc: Document) => void) | undefined,
  canDelete: CanDeleteDocument | undefined,
  doc: Document,
): boolean {
  return Boolean(onDelete) && (!canDelete || canDelete(doc));
}
